import { PricedSearchResult } from './queries';

/**
 * Helpers del vertical de PASES de tren (Interrail / Eurail).
 *
 * Un pase no es un trayecto: habilita a viajar por una RED durante una
 * vigencia, así que la búsqueda no tiene origen ni destino. La familia del
 * producto la decide el PROVEEDOR según la residencia del pasajero —
 * Interrail para residentes europeos, Eurail para el resto — y por eso no se
 * le pregunta al asesor: elegir la equivocada devuelve cero resultados.
 */

export type PassOffer = PricedSearchResult & {
  /** Slug del proveedor: "eurail-one-country-pass-greece". */
  product?: string;
  travelClass?: '1stclass' | '2ndclass';
  /**
   * Días de viaje del pase. AUSENTE = pase CONTINUO (se viaja todos los días
   * de la vigencia); no es un dato faltante.
   */
  travelDayCount?: number;
  validityDays?: number;
  validityPeriod?: string;
  passengers?: { count: number; type: string }[];
};

/** Un pase sin días de viaje declarados es continuo. */
export function isContinuous(offer: PassOffer): boolean {
  return typeof offer.travelDayCount !== 'number';
}

/** Familia comercial del producto, que define la red y el público. */
export function passFamily(offer: PassOffer): 'interrail' | 'eurail' | 'other' {
  const product = offer.product ?? '';
  if (product.startsWith('interrail')) return 'interrail';
  if (product.startsWith('eurail')) return 'eurail';
  return 'other';
}

/**
 * Países de la red, en el slug del producto → ISO-2, para poder mostrarlos
 * traducidos. Es la lista de "one country pass" que publica el proveedor; lo
 * que no esté acá cae al texto del slug (nunca se inventa un nombre).
 */
const COUNTRY_SLUGS: Record<string, string> = {
  austria: 'AT', belgium: 'BE', bulgaria: 'BG', croatia: 'HR',
  'czech-republic': 'CZ', czechia: 'CZ', denmark: 'DK', estonia: 'EE',
  finland: 'FI', france: 'FR', germany: 'DE', 'great-britain': 'GB',
  greece: 'GR', hungary: 'HU', ireland: 'IE', italy: 'IT', latvia: 'LV',
  lithuania: 'LT', luxembourg: 'LU', macedonia: 'MK', montenegro: 'ME',
  netherlands: 'NL', norway: 'NO', poland: 'PL', portugal: 'PT',
  romania: 'RO', serbia: 'RS', slovakia: 'SK', slovenia: 'SI', spain: 'ES',
  sweden: 'SE', switzerland: 'CH', turkey: 'TR',
};

const displayNames = new Map<string, Intl.DisplayNames | null>();

function regionNames(language: string): Intl.DisplayNames | null {
  if (!displayNames.has(language)) {
    try {
      displayNames.set(language, new Intl.DisplayNames([language], { type: 'region' }));
    } catch {
      displayNames.set(language, null);
    }
  }
  return displayNames.get(language) ?? null;
}

/** Título legible del producto a partir del slug del proveedor. */
export function productTitle(offer: PassOffer, language: string): string {
  const product = offer.product ?? '';
  if (!product) return '—';

  const [lang] = (language || 'es').split('-');
  const family = passFamily(offer);
  const familyLabel = family === 'other' ? '' : family === 'eurail' ? 'Eurail' : 'Interrail';

  // "eurail-one-country-pass-greece" → scope "one-country-pass", país "greece"
  const rest = product.replace(/^(eurail|interrail)-/, '');
  if (rest.startsWith('global-pass')) {
    return `${familyLabel} Global Pass`.trim();
  }
  const countrySlug = rest.replace(/^one-country-pass-/, '');
  const iso = COUNTRY_SLUGS[countrySlug];
  const country = iso
    ? (regionNames(lang)?.of(iso) ?? countrySlug)
    : countrySlug.replace(/-/g, ' ');
  return `${familyLabel} ${country}`.trim();
}

/** Los pases de un mismo producto, con sus variantes de días y clase. */
export interface PassGroup {
  product: string;
  offers: PassOffer[];
  /** El más barato del grupo: es el que se muestra en el encabezado. */
  cheapest: PassOffer;
}

/**
 * El proveedor devuelve un bundle por combinación (producto × días × clase):
 * 300+ filas planas. Se agrupan por PRODUCTO para que el agente elija primero
 * la red y después la variante, igual que en trenes con las tarifas.
 */
export function groupPasses(offers: PassOffer[]): PassGroup[] {
  const groups = new Map<string, PassOffer[]>();
  for (const offer of offers) {
    const key = offer.product ?? 'other';
    groups.set(key, [...(groups.get(key) ?? []), offer]);
  }

  return [...groups.entries()]
    .map(([product, list]) => {
      const sorted = [...list].sort(
        (a, b) => a.pricing.final.amountMinor - b.pricing.final.amountMinor,
      );
      return { product, offers: sorted, cheapest: sorted[0] };
    })
    .sort((a, b) => a.cheapest.pricing.final.amountMinor - b.cheapest.pricing.final.amountMinor);
}

export interface PassFilters {
  travelClass: '' | '1stclass' | '2ndclass';
  /** Días de viaje mínimos; 0 = sin filtro. */
  minTravelDays: number;
  /** Solo pases de red completa (global), no de un país. */
  globalOnly: boolean;
  /** Texto libre para encontrar el país del producto. */
  query: string;
}

export const EMPTY_PASS_FILTERS: PassFilters = {
  travelClass: '',
  minTravelDays: 0,
  globalOnly: false,
  query: '',
};

export function filterPasses(
  offers: PassOffer[],
  filters: PassFilters,
  language: string,
): PassOffer[] {
  const needle = filters.query.trim().toLowerCase();
  return offers.filter((offer) => {
    if (filters.travelClass && offer.travelClass !== filters.travelClass) return false;
    if (filters.globalOnly && !(offer.product ?? '').includes('global-pass')) return false;
    if (filters.minTravelDays) {
      // Un pase continuo cubre cualquier cantidad de días de viaje.
      const days = offer.travelDayCount;
      if (typeof days === 'number' && days < filters.minTravelDays) return false;
    }
    if (needle) {
      const haystack = `${offer.product ?? ''} ${productTitle(offer, language)}`.toLowerCase();
      if (!haystack.includes(needle)) return false;
    }
    return true;
  });
}

/**
 * Los pases se pueden BUSCAR y cotizar, pero hoy no se pueden confirmar: el
 * `createOrder` de All Aboard rechaza cualquier orden armada a partir de un
 * `passBundle` con "Unable to process booking". Está aislado del lado del hub
 * (misma cuenta y mismos datos ordenan boletos y no ordenan pases) y en manos
 * del proveedor.
 *
 * Mientras tanto el buscador queda vivo —cotizar sirve— pero reservar se
 * bloquea de entrada, para que el asesor no lo descubra después de cargar los
 * pasajeros. QUITAR ESTA CONSTANTE cuando All Aboard lo resuelva.
 */
export const PASSES_BOOKING_BLOCKED = true;
